/**
 * Auth Mock Service - Servicio de autenticación simulado
 * Para desarrollo y pruebas sin Firebase
 */

import type { User } from '../../../shared/types';
import { LoggerService } from '../../../shared/services/logger.service';

export class AuthMockService {
  private currentUser: User | null = null;
  private authStateCallbacks: ((user: User | null) => void)[] = [];

  async login(email: string, password: string): Promise<User> {
    LoggerService.info('Mock login attempt', 'AuthMockService', { email });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Usuarios mock predefinidos
    const mockUsers: { [key: string]: User } = {
      'test@invirtiendo.com': {
        uid: 'mock-user-1',
        email: 'test@invirtiendo.com',
        displayName: 'Usuario de Prueba'
      },
      'admin@invirtiendo.com': {
        uid: 'mock-admin-1',
        email: 'admin@invirtiendo.com',
        displayName: 'Administrador'
      }
    };

    // Verificar credenciales
    if (mockUsers[email] && password === 'test123456') {
      this.currentUser = mockUsers[email];
      this.notifyAuthStateChange();
      LoggerService.logUserAction('mock_login_success', 'AuthMockService', { email });
      return this.currentUser;
    }

    // Simular error de autenticación
    const error = new Error('Credenciales inválidas');
    LoggerService.error('Mock login failed', 'AuthMockService', { email, error });
    throw error;
  }

  async register(email: string, password: string, displayName?: string): Promise<User> {
    LoggerService.info('Mock register attempt', 'AuthMockService', { email, displayName });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Verificar que la contraseña sea válida
    if (password.length < 6) {
      const error = new Error('La contraseña debe tener al menos 6 caracteres');
      LoggerService.error('Mock register failed', 'AuthMockService', { email, error });
      throw error;
    }

    // Crear nuevo usuario mock
    const newUser: User = {
      uid: `mock-user-${Date.now()}`,
      email: email,
      displayName: displayName || email.split('@')[0]
    };

    this.currentUser = newUser;
    this.notifyAuthStateChange();
    LoggerService.logUserAction('mock_register_success', 'AuthMockService', { email });
    return this.currentUser;
  }

  async logout(): Promise<void> {
    LoggerService.info('Mock logout', 'AuthMockService');
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 200));
    
    this.currentUser = null;
    this.notifyAuthStateChange();
    LoggerService.logUserAction('mock_logout', 'AuthMockService');
  }

  onAuthStateChange(callback: (user: User | null) => void): () => void {
    this.authStateCallbacks.push(callback);
    
    // Notificar estado actual inmediatamente
    callback(this.currentUser);
    
    // Retornar función de unsubscribe
    return () => {
      const index = this.authStateCallbacks.indexOf(callback);
      if (index > -1) {
        this.authStateCallbacks.splice(index, 1);
      }
    };
  }

  private notifyAuthStateChange(): void {
    this.authStateCallbacks.forEach(callback => {
      callback(this.currentUser);
    });
  }

  // Métodos adicionales para desarrollo
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }
}
